import _ from 'lodash';
import moment from 'moment';
import { default as React, useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Link as DefaultLink, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import { CompanyFilter, Currency, DateTime, ExportDropdown, Widget } from '~/components';
import CurrencyFilter from '~/components/filters/CurrencyFilter';
import FilterButton from '~/components/filters/FilterButton';
import OpportunityFiltersBar from '~/components/filters/OpportunityFiltersBar';
import OpportunityFiltersGroup from '~/components/filters/OpportunityFiltersGroup';
import ReportPeriodFilter from '~/components/filters/ReportPeriodFilter';
import { useApi, useConfirmation, useIntegrations, useWorkspace } from '~/contexts';
import {
  useDocumentTitle,
  useFeatures,
  useIsMounted,
  useNumberFormat,
  useSearchParams,
  useSearchParamsConfig,
} from '~/hooks';
import PageLoader from '~/routes/public/pages/PageLoader';
import { colors, weights } from '~/styles';
import { QuerySort, QueryString, dateFormats, intervalOptions, mimeTypes } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import FiltersGroup from '~/components/filters/FiltersGroup.jsx';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const Link = styled(DefaultLink)`
  color: ${colors.black};
`;

function OpportunitiesByMonth({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();
  const integrations = useIntegrations();
  const isMounted = useIsMounted();

  const [query, setQuery] = useState({ report: null, status: 'loading', loading: { table: false } });
  const [params, setParams] = useState({
    period: null,
    opportunityOpenedPeriod: null,
    opportunityClosedPeriod: null,
    companies: [],
    opportunityHubspotSynced: null,
    opportunitySalesforceSynced: null,
    opportunitySalesforceRecordTypes: [],
    opportunityStageCategories: [],
    opportunityStages: [],
    opportunityOwners: [],
    opportunityPipelines: [],
    opportunityTags: [],
    opportunityTypes: [],
    opportunityAmountGreaterThanOrEqual: null,
    opportunityPractices: [],
    opportunityBusinessUnits: [],
    currency: null,
    sort: new QuerySort('start', 'desc'),
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: { ...reportsSearchParamsConfig.period, default: intervalOptions.this_year },
        opportunityOpenedPeriod: reportsSearchParamsConfig.opportunityOpenedPeriod,
        opportunityClosedPeriod: reportsSearchParamsConfig.opportunityClosedPeriod,
        companies: searchParamsConfig.companies,
        opportunitySalesforceSynced: searchParamsConfig.opportunitySalesforceSynced,
        opportunityHubspotSynced: searchParamsConfig.opportunityHubspotSynced,
        opportunityStageCategories: searchParamsConfig.opportunityStageCategories,
        opportunityStages: searchParamsConfig.opportunityStages,
        opportunityOwners: searchParamsConfig.opportunityOwners,
        opportunityPipelines: searchParamsConfig.opportunityPipelines,
        opportunityTags: searchParamsConfig.opportunityTags,
        opportunityTypes: searchParamsConfig.opportunityTypes,
        opportunityAmountGreaterThanOrEqual: searchParamsConfig.opportunityAmountGreaterThanOrEqual,
        opportunitySalesforceRecordTypes: searchParamsConfig.opportunitySalesforceRecordTypes,
        opportunityPractices: searchParamsConfig.opportunityPractices,
        opportunityBusinessUnits: searchParamsConfig.opportunityBusinessUnits,
        currency: searchParamsConfig.currency,
        sort: { default: new QuerySort('start', 'asc'), ...searchParamsConfig.sort },
      }),
      [searchParamsConfig, reportsSearchParamsConfig],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      opportunityOpenedStart: params.opportunityOpenedPeriod?.start ?? undefined,
      opportunityOpenedEnd: params.opportunityOpenedPeriod?.end ?? undefined,
      opportunityClosedStart: params.opportunityClosedPeriod?.start ?? undefined,
      opportunityClosedEnd: params.opportunityClosedPeriod?.end ?? undefined,
      companyId: params.companies.map((v) => v.id),
      opportunitySalesforceSynced:
        integrations?.salesforce && params.opportunitySalesforceSynced ? params.opportunitySalesforceSynced : undefined,
      opportunityHubspotSynced:
        integrations?.hubspot && params.opportunityHubspotSynced ? params.opportunityHubspotSynced : undefined,
      opportunityStageCategoryId: params.opportunityStageCategories.map((v) => v.id),
      opportunityStageId: params.opportunityStages.length ? params.opportunityStages.map((v) => v.id) : [],
      opportunityOwnerId: params.opportunityOwners.map((v) => v.id),
      opportunityPipelineId: params.opportunityPipelines.map((v) => v.id),
      opportunityTagId: params.opportunityTags.length ? params.opportunityTags.map((v) => v.id) : [],
      opportunityTypeId: params.opportunityTypes.map((v) => v.id),
      opportunityAmountGreaterThanOrEqual: params.opportunityAmountGreaterThanOrEqual ?? undefined,
      opportunitySalesforceRecordType:
        integrations?.salesforce && params.opportunitySalesforceRecordTypes?.length
          ? params.opportunitySalesforceRecordTypes.map((v) => v.id)
          : undefined,
      opportunityPracticeId: params.opportunityPractices.map((v) => v.id),
      opportunityBusinessUnitId: params.opportunityBusinessUnits.map((v) => v.id),
      currency: params.currency ?? undefined,
      sort: params.sort,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    [params, integrations?.salesforce, integrations?.hubspot],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    if (!isMounted.current) return;

    setQuery((state) => ({
      ...state,
      data,
      status: 'ready',
      loading: {
        table: false,
      },
    }));
  }, [api, workspace.id, report.id, urlSearchParams, isMounted]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, table: true } }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleCurrencyChange = (currency) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, summary: true, table: true } }));
    setParams({ ...params, page: 0, currency });
    searchParams.set({ currency });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        {(params.period?.start || params.period?.end) && (
          <ReportPeriodFilter
            clearable={false}
            value={params.period}
            placeholder="Opened Date Range"
            scope="month"
            onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
          />
        )}

        {(params.opportunityClosedPeriod?.start || params.opportunityClosedPeriod?.end) && (
          <ReportPeriodFilter
            value={params.opportunityClosedPeriod}
            placeholder="Expected Close Date Range"
            onChange={({ target: { value } }) => handleApplyFilters({ opportunityClosedPeriod: value })}
          />
        )}

        {features.multicurrency && (
          <CurrencyFilter value={params.currency} onChange={({ target: { value } }) => handleCurrencyChange(value)} />
        )}

        {!_.isEmpty(params.companies) && (
          <CompanyFilter
            name="companies"
            value={params.companies}
            onChange={({ target: { value } }) => handleApplyFilters({ companies: value })}
          />
        )}

        <OpportunityFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} onCurrencyChange={handleCurrencyChange} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const { workspace } = useWorkspace();
  const report = query.data;
  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const getOpportunitiesRoute = (oppData) =>
    `/app/${workspace.key}/reports/library/opportunities?${new QueryString({ ...oppData, opportunityStageCategoryId: 'all' })}`;

  return (
    <>
      <Report.Summary>
        <TimeSeriesGraph report={report} />
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem" name="start" onSort={onSort} sort={params.sort}>
              Month
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Opened
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Closing
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Weighted Opened
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Gross Opened
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Weighted Closing
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Gross Closing
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {report.records.map((month) => (
              <Table.Row key={month.start}>
                <Table.Cell>
                  <DateTime value={month.start} format={'MMMM, YYYY'} />
                </Table.Cell>
                <Table.Cell>
                  <Link
                    to={getOpportunitiesRoute({
                      opportunityOpenedStart: month.start,
                      opportunityOpenedEnd: month.end,
                    })}>
                    {month.openedCount}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Link
                    to={getOpportunitiesRoute({
                      opportunityClosedStart: month.start,
                      opportunityClosedEnd: month.end,
                    })}>
                    {month.closingCount}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Currency value={month.openedConvertedWeightedAmount} currency={report.currency} />
                </Table.Cell>
                <Table.Cell>
                  <Currency value={month.openedConvertedAmount} currency={report.currency} />
                </Table.Cell>
                <Table.Cell>
                  <Currency value={month.closingConvertedWeightedAmount} currency={report.currency} />
                </Table.Cell>
                <Table.Cell>
                  <Currency value={month.closingConvertedAmount} currency={report.currency} />
                </Table.Cell>
              </Table.Row>
            ))}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell>{numberFormat.format(report.totals.openedCount)}</Table.Cell>
              <Table.Cell>{numberFormat.format(report.totals.closingCount)}</Table.Cell>
              <Table.Cell>
                <Currency value={report.totals.openedConvertedWeightedAmount} currency={report.currency} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={report.totals.openedConvertedAmount} currency={report.currency} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={report.totals.closingConvertedWeightedAmount} currency={report.currency} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={report.totals.closingConvertedAmount} currency={report.currency} />
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={report.records.length}
            label="Month"
            isLoading={query.status !== 'ready'}
            partial={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        clearable={false}
        value={filters.period}
        placeholder="Opened Date Range"
        scope="month"
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <FiltersGroup label="Company Filters" filters={[filters.companies]}>
        <CompanyFilter
          name="companies"
          value={filters.companies}
          onChange={({ target: { value } }) => handleFilter({ companies: value })}
        />
      </FiltersGroup>

      <OpportunityFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

function TimeSeriesGraph({ report }) {
  const history = useHistory();
  const { workspace } = useWorkspace();

  const data = report.records;

  // Preparing the data for the chart
  const chartData = {
    labels: data.map((record) => moment(record.start).format(dateFormats.monthYear)),
    datasets: getDatasets(data),
  };

  const handleClick = (e, [element]) => {
    if (!element) return;
    const month = e.chart.data.labels[element.index];
    const period = {
      start: moment(month).format(dateFormats.isoDate),
      end: moment(month).endOf('month').format(dateFormats.isoDate),
    };
    const params = e.chart.data.datasets[element.datasetIndex].label.includes('Opened')
      ? {
          opportunityOpenedStart: period.start,
          opportunityOpenedEnd: period.end,
          opportunityStageCategoryId: 'all',
          currency: report.currency,
        }
      : {
          opportunityClosedStart: period.start,
          opportunityClosedEnd: period.end,
          opportunityStageCategoryId: 'all',
          currency: report.currency,
        };

    history.push(`/app/${workspace.key}/reports/library/opportunities?${new QueryString(params)}`);
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        onClick: null,
        labels: {
          pointStyleWidth: 10,
          boxHeight: 7,
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {},
      },
    },
    scales: {
      x: {
        grid: { color: colors.grey10, drawBorder: false },
        stacked: true,
        ticks: {
          color: colors.grey40,
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
      y: {
        grid: { color: colors.grey10, drawBorder: false },
        stacked: true,
        min: 0,
        beginAtZero: true,
      },
    },

    callbacks: {
      label: function (tooltipItem, data) {
        let label = data.datasets[tooltipItem.datasetIndex].label || '';
        let value = tooltipItem.yLabel;
        let comment = '';

        if (tooltipItem.datasetIndex === 0 || tooltipItem.datasetIndex === 1) {
          // For 'Opened' datasets
          comment = data.labels[tooltipItem.index].commentCreated;
        } else {
          // For 'Closing' datasets
          comment = data.labels[tooltipItem.index].commentClosed;
        }

        return `${label}: ${value} (${comment})`;
      },
    },

    onHover: (e, chartElement) => {
      e.native.target.style.cursor = chartElement.length ? 'pointer' : 'default';
    },

    onClick: handleClick,
  };

  return (
    <Widget style={{ height: '20rem' }}>
      <Bar data={chartData} options={options} />
    </Widget>
  );
}

const getDatasets = (data) => [
  {
    label: 'Weighted Opened',
    data: data.map((item) => item.openedConvertedWeightedAmount),
    backgroundColor: colors.primary50,
    stack: 'Weighted Opened',
  },
  {
    label: 'Gross Opened',
    data: data.map((item) => item.openedConvertedAmount),
    backgroundColor: colors.primary10,
    stack: 'Gross Opened',
  },
  {
    label: 'Weighted Closing',
    data: data.map((item) => item.closingConvertedWeightedAmount),
    backgroundColor: colors.success100,
    stack: 'Weighted Closing',
  },
  {
    label: 'Gross Closing',
    data: data.map((item) => item.closingConvertedAmount),
    backgroundColor: colors.success10,
    stack: 'Gross Closing',
  },
];

export default OpportunitiesByMonth;
